import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ExaminationPageLayout } from 'pages/ExaminationPage/ExaminationPageLayout';
import { toast } from 'react-toastify';
import { Button, Heading, SaveIcon, SurveyIcon, isMobileQuery } from '@higo/ui';
import { format } from 'date-fns';
import { FormProvider, useForm } from 'react-hook-form';
import useMedia from 'react-use/lib/useMedia';
import {
  ExaminationPageDiagnosisFormValue,
  mapToExaminationDoctorDiagnosisDTO,
  mapToExaminationPageDiagnosisFormValue,
} from '@higo/common/lib/features/ExaminationPage/models';
import {
  AbdominalAuscultationSection,
  CoughAuscultationSection,
  HeartAuscultationSection,
  LungsAuscultationSection,
  AnamnesisSection,
  ExaminationPageSummarySection,
} from '@higo/common/lib/features/ExaminationPage/sections';
import { SkinExaminationSectionForVisits } from '@higo/doctor/src/pages/ExaminationPage/components/SkinExaminationSectionForVisits';
import { EarsExaminationSectionForVisits } from '@higo/doctor/src/pages/ExaminationPage/components/EarsExaminationSectionForVisits';
import { ThroatExaminationSectionForVisits } from '@higo/doctor/src/pages/ExaminationPage/components/ThroatExaminationSectionForVisits';
import {
  ExaminationPageHeader,
  ExaminationPageOverview,
  ExaminationPageInfoBarForVisits,
  ExaminationPageGeneralSurveyModal
} from '@higo/common/lib/features/ExaminationPage/components';
import { isNil } from 'rambda';
import {
  RequestGenerateLeftEarExaminationVideoButton,
  RequestGenerateRightEarExaminationVideoButton,
  RequestGenerateSkinExaminationVideoButton,
  RequestGenerateThroatExaminationVideoButton,
} from './components/RequestGenerateVisualExaminationVideoButton';
import { useVisitExaminationPageModel } from '@higo/common/lib/features/ExaminationPage/hooks';
import { PhysicalExaminationType } from '@higo/api';
import { HeartRateMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/HeartRateMeasurementEntry';
import { TemperatureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/TemperatureMeasurementEntry';
import { BloodPressureMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/BloodPressureMeasurementEntry';
import { BloodSaturationMeasurementEntry } from '@higo/common/lib/features/ExaminationPage/sections/VitalSignsSection/BloodSaturationMeasurementEntry';
import { useConnectWebSocket } from './webSocketsForVisits/connectWebSocket';
import { VisitsLiveNewWebsocketExam } from './VisitsLiveNewWebsocketExam';
import { CloseVisitButton } from './components/buttons/CloseVisitButton';
import { media } from '@higo/ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSaveFinalExaminationDiagnosisMutation } from 'pages/ExaminationPage/hooks/useSaveFinalExaminationDiagnosisMutation';
import {
  ExaminationPageDiagnosisAutoSave,
  ExaminationPageDiagnosisAutoSaveRef,
} from './ExaminationPageDiagnosisAutoSave';
import { useExaminationDiagnosisQuery } from '@higo/common/lib/features/ExaminationPage/hooks/queries/useExaminationDiagnosisQuery';
import { useVideoProcessQuery } from 'hooks/queries/useVideoProcessQuery';
import { parseJSON } from 'date-fns';
import { ConstraintContainer } from 'components/ConstraintContainer';
import { HorizontalDivider } from '@higo/ui';
import { ReactQueryAutoSaveSaveStatus } from '@higo/common/lib/hooks/useReactQueryAutoSave';
import { UnassignFromVisitPageButton } from './components/buttons/UnassignFromVisitPageButton';
import { AssignFromVisitPageButton } from './components/buttons/AssignFromVisitPageButton';
import { coerceNumber } from '@higo/common/lib/utils';
import { ShareExaminationModalForVisits } from 'components/ShareExaminationModal/ShareExaminationModalForVisits';
import { useUserPermissions } from 'hooks/useUserPermissions';
import { PatientExaminationsModal } from 'components/PatientExaminationsModal';

const ExamPageStyledHeader = styled(ExaminationPageHeader)`
  // height: 180px !important;
  ${media.desktop.min} {
    // height: 165px !important;
  }
`;

const VisitsContainer = styled.div`
  #heartRate-0:first-of-type {
    background-color: #eef0f4;
    padding: 5px;
    border-radius: 8px;
    height: 82px;
    max-width: 400px;
  }

  #temperature-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }

  #bloodPressure-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }

  #bloodSaturation-0:first-of-type {
    background-color: #eef0f4;
    border-radius: 8px;
    height: 82px;
    padding: 5px;
    max-width: 400px;
  }
`;
const MeasurementContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 1.5rem;
`;

const MeasurementItem = styled.div`
  flex: 1 1 auto;
  min-width: 80px;
`;

const ExamDate = styled.div`
  font-size: 12.8px;
  font-weight: 700;
  color: #72799c;
  margin-bottom: 0.5rem;
`;

const SendButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const DraftButtonContainer = styled.div`
  display: none;
`;

export type ExaminationPageParams = {
  id: string;
};

type ExaminationType = {
  examination: any;
  isCanceled: boolean;
  isStartedOnDevice?: boolean;
  examinationId: number;
};

const BLOOD_PRESSURE_EXAM = 'BLOOD_PRESSURE_EXAM';
const BLOOD_SATURATION_EXAM = 'BLOOD_SATURATION_EXAM';

export const VisitsLiveExaminationPage = () => {
  const location = useLocation();
  const params = useParams();
  const isMobileView = useMedia(`${isMobileQuery}`);
  const { visitAssignmentPermission } = useUserPermissions();
  const assignmentPermission = visitAssignmentPermission?.create;


  const state = (location.state as
    | { visitId?: number; webSocketData?: any }
    | null) ?? null;

  const visitId = state?.visitId ?? coerceNumber(params.id);
  const webSocketData = state?.webSocketData ?? null;
  const model = useVisitExaminationPageModel(visitId);
  const [webSocketMessage, setWebSocketMessage] = useState<any | null>(null);
  const [examIds, setExamIds] = useState<
    { examId: string; examDate: string }[]
  >([]);
  const [skinExams, setSkinExams] = useState<ExaminationType[]>([]);
  const [throatExams, setThroatExams] = useState<ExaminationType[]>([]);
  const [abdominalExams, setAbdominalExams] = useState<ExaminationType[]>([]);
  const [leftEarExams, setLeftEarExams] = useState<ExaminationType[]>([]);
  const [rightEarExams, setRightEarExams] = useState<ExaminationType[]>([]);
  const [coughExams, setCoughExams] = useState<ExaminationType[]>([]);
  const [lungsExams, setLungsExams] = useState<ExaminationType[]>([]);
  const [heartExams, setHeartExams] = useState<ExaminationType[]>([]);
  const [heartRateExams, setHeartRateExams] = useState<ExaminationType[]>([]);
  const [temperatureExams, setTemperatureExams] = useState<ExaminationType[]>(
    [],
  );
  const [bloodPressureExams, setBloodPressureExams] = useState<
    ExaminationType[]
  >([]);
  const [bloodSaturationExams, setBloodSaturationExams] = useState<
    ExaminationType[]
  >([]);

  const [lungsAsculationsPoints, setLungsAsculationsPoints] = useState<any[]>(
    [],
  );
  const [heartAsculationsPoints, setHeartAsculationsPoints] = useState<any[]>(
    [],
  );
  const [abdominalAsculationsPoints, setAbdominalAsculationsPoints] = useState<
    any[]
  >([]);
  const autoSaveRef = useRef<ExaminationPageDiagnosisAutoSaveRef>(null);

  const [autoSaveState, setAutoSaveState] =
    useState<ReactQueryAutoSaveSaveStatus>();
  const isDiagnosisId = useMemo(
    () =>
      model?.details.diagnosisId !== null &&
      model?.details.diagnosisId !== undefined &&
      model?.details.diagnosisId !== 0,
    [model?.details.diagnosisId],
  );
  const { data: diagnosisData } = useExaminationDiagnosisQuery(
    visitId,
    isDiagnosisId,
    true,
  );

  const methods = useForm({
    defaultValues: mapToExaminationPageDiagnosisFormValue(diagnosisData),
  });

  useEffect(() => {
    if (diagnosisData) {
      methods.reset(mapToExaminationPageDiagnosisFormValue(diagnosisData));
    }
  }, [diagnosisData, methods]);

  const { mutateAsync: saveFinalDiagnosis } =
    useSaveFinalExaminationDiagnosisMutation(model?.details.id || 0, true);
  const handleDiagnosisSubmit = (value: ExaminationPageDiagnosisFormValue) => {
    const dto = mapToExaminationDoctorDiagnosisDTO(value);
    dto && saveFinalDiagnosis(dto);
  };

  const { subscribe, unsubscribe, isConnected } =
    useConnectWebSocket(webSocketData);

  const intl = useIntl();

  const onSaveDraftButtonClick = () => {
    autoSaveRef?.current?.saveDraft();
    autoSaveState === 'saved' &&
      toast.success(
        intl.formatMessage({
          id: 'toast.diagnosis.savedAsDraft',
        }),
      );
  };
  useEffect(() => {
    const skin: ExaminationType[] = [];
    const throat: ExaminationType[] = [];
    const abdominal: ExaminationType[] = [];
    const leftEar: ExaminationType[] = [];
    const rightEar: ExaminationType[] = [];
    const cough: ExaminationType[] = [];
    const lungs: ExaminationType[] = [];
    const heart: ExaminationType[] = [];
    const heartRate: ExaminationType[] = [];
    const temperature: ExaminationType[] = [];
    const bloodPressure: ExaminationType[] = [];
    const bloodSaturation: ExaminationType[] = [];

    model?.examinations.forEach((examination: any, examinationId: number) => {
      const isCanceled =
        examination.physicalExaminationDetails[0].examStatus ===
          'CANCELED_BY_USER' ||
        examination.physicalExaminationDetails[0].examStatus ===
          'STARTED_ON_DEVICE';

      const examType = examination.physicalExaminationDetails[0].examType;

      switch (examType) {
        case PhysicalExaminationType.Skin:
          skin.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Throat:
          throat.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.AbdominalAuscultation:
          abdominal.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.LeftEar:
          leftEar.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.RightEar:
          rightEar.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Cough:
          cough.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.LungsAuscultation:
          lungs.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.HeartAuscultation:
          heart.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.HeartRateWav:
          heartRate.push({ examination, isCanceled, examinationId });
          break;
        case PhysicalExaminationType.Temperature:
          temperature.push({ examination, isCanceled, examinationId });
          break;
        case BLOOD_PRESSURE_EXAM:
          bloodPressure.push({ examination, isCanceled, examinationId });
          break;
        case BLOOD_SATURATION_EXAM:
          bloodSaturation.push({ examination, isCanceled, examinationId });
          break;
        default:
          break;
      }
    });

    setSkinExams(skin);
    setThroatExams(throat);
    setAbdominalExams(abdominal);
    setLeftEarExams(leftEar);
    setRightEarExams(rightEar);
    setCoughExams(cough);
    setLungsExams(lungs);
    setHeartExams(heart);
    setHeartRateExams(heartRate);
    setTemperatureExams(temperature);
    setBloodPressureExams(bloodPressure);
    setBloodSaturationExams(bloodSaturation);
  }, [model]);

  useEffect(() => {
    if (webSocketData) {
      const handleMessage = (message: { body: string }) => {
        setWebSocketMessage(JSON.parse(message.body));

        const body = JSON.parse(message.body);

        const newExamId = body.data.examId;
        const newExamDate = body.data.sessionEndTime;

        setExamIds((prevExamIds) => {
          if (prevExamIds.some((exam) => exam.examId === newExamId))
            return prevExamIds;

          const newExam = { examId: newExamId, examDate: newExamDate };
          return [newExam, ...prevExamIds];
        });

        if (isConnected) {
          body.data.physicalExaminationType === 'LUNGS_AUSCULTATION_EXAM' &&
            setLungsAsculationsPoints((prevLungsAsculationsPoints) => [
              ...prevLungsAsculationsPoints,
              body.data,
            ]);

          body.data.physicalExaminationType === 'HEART_AUSCULTATION_EXAM' &&
            setHeartAsculationsPoints((prevHeartAsculationsPoints) => [
              ...prevHeartAsculationsPoints,
              body.data,
            ]);

          body.data.physicalExaminationType === 'ABDOMINAL_AUSCULTATION_EXAM' &&
            setAbdominalAsculationsPoints((prevAbdominalAsculationsPoints) => [
              ...prevAbdominalAsculationsPoints,
              body.data,
            ]);
        }
      };
      subscribe(handleMessage);
      return () => {
        unsubscribe();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    webSocketData &&
      isConnected &&
      toast.success('You have a live connection with the patient', {
        autoClose: 6000,
      });
    webSocketData &&
      !isConnected &&
      toast.warning(
        'There has been an error during live connection with the server, visit is ongoing. Please refresh the page to see new exams',
        { autoClose: 10000 },
      );
  }, [webSocketData, isConnected]);

  const setExamsAfterWebSocket = (
    newExam: any,
    setExamFunction: (value: any) => void,
  ) => {
    setExamFunction((prevExams: any[]) => {
      const updatedExams = [
        ...prevExams,
        {
          examination: {
            examinationId: Number(newExam.examId),
            sessionEndTime: Date.now(),
          },
          isCanceled: false,
          examinationId: Number(newExam.examId),
        },
      ].sort(
        (a, b) =>
          new Date(b.examination.sessionEndTime).getTime() -
          new Date(a.examination.sessionEndTime).getTime(),
      );
      return updatedExams;
    });
  };

  useEffect(() => {
    if (webSocketMessage) {
      const newExam = webSocketMessage.data;
      switch (newExam.physicalExaminationType) {
        case PhysicalExaminationType.Skin:
          setExamsAfterWebSocket(newExam, setSkinExams);
          break;
        case PhysicalExaminationType.Throat:
          setExamsAfterWebSocket(newExam, setThroatExams);
          break;
        case PhysicalExaminationType.LeftEar:
          setExamsAfterWebSocket(newExam, setLeftEarExams);
          break;
        case PhysicalExaminationType.RightEar:
          setExamsAfterWebSocket(newExam, setRightEarExams);
          break;
        case PhysicalExaminationType.Temperature:
          setExamsAfterWebSocket(newExam, setTemperatureExams);
          break;
        case BLOOD_PRESSURE_EXAM:
          setExamsAfterWebSocket(newExam, setBloodPressureExams);
          break;
        case BLOOD_SATURATION_EXAM:
          setExamsAfterWebSocket(newExam, setBloodSaturationExams);
          break;
        case PhysicalExaminationType.HeartRateWav:
          setExamsAfterWebSocket(newExam, setHeartRateExams);
          break;
        case PhysicalExaminationType.Cough:
          setExamsAfterWebSocket(newExam, setCoughExams);
          break;

        case PhysicalExaminationType.AbdominalAuscultation:
          break;
        case PhysicalExaminationType.LungsAuscultation:
          break;
        case PhysicalExaminationType.HeartAuscultation:
          break;

        default:
          break;
      }
    }
  }, [webSocketMessage]);

  useVideoProcessQuery(Number(visitId));

  const examDate = (date: Date | undefined) =>
    date && format(new Date(parseJSON(date)), 'dd/MM/yyyy HH:mm:ss');
  return !isNil(model) ? (
    <VisitsContainer>
      <ExamPageStyledHeader>
        <ExaminationPageLayout>
          <ExaminationPageOverview
            mode={model.mode}
            examinationId={visitId}
            patient={model.details.patient}
            patientOverview={model.patientOverview}
            patientExaminationsModalSlot={
              <PatientExaminationsModal patientId={model.details.patient.id} />
            }
            isVisitOverview
            isVisitEnd={model.details.status === 'ENDED'}
            webSocketData={webSocketData}
            generalSurveySlot={
              model.answeredGeneralSurvey && (
                <ExaminationPageGeneralSurveyModal
                  answeredGeneralSurvey={model.answeredGeneralSurvey}
                  hasCopyFeature
                  hasToastWhenCopy
                  trigger={
                    model.mode === 'FAMILY' ? (
                      <Button
                        color="secondary"
                        size="small"
                        endIcon={<SurveyIcon />}
                        style={
                          isMobileView
                            ? { width: '70%', margin: '0.4rem 0.4rem 0 0.4rem' }
                            : { width: '13.5rem' }
                        }
                        id="general-survey-button"
                      >
                        <FormattedMessage id="generalSurvey.modalTrigger.title" />
                      </Button>
                    ) : (
                      <></>
                    )
                  }
                />
              )
            }
          />
        </ExaminationPageLayout>
      </ExamPageStyledHeader>

      <ExaminationPageLayout>
        <ExaminationPageInfoBarForVisits
          examinationCreateDate={model?.details?.createDate}
          partnerName={model.details.patient.partnerDetails?.name}
          executedBy={model.details.executedBy}
          shareModalSlot={
            <ShareExaminationModalForVisits
              visitId={visitId}
              examData={model}
            />
          }
          hasOperatorData={true}
        />
      </ExaminationPageLayout>
      <ExaminationPageLayout>
        {' '}
        {webSocketData ? (
          <CloseVisitButton visitId={visitId}></CloseVisitButton>
        ) : (
          <div style={{ height: '3rem' }}></div>
        )}
      </ExaminationPageLayout>
      <FormProvider {...methods}>
        <ExaminationPageDiagnosisAutoSave
          ref={autoSaveRef}
          examinationId={model.details.id ?? 0}
          onStatusChange={setAutoSaveState}
          isVisitDiagnosis={true}
        />
        <form onSubmit={methods.handleSubmit(handleDiagnosisSubmit)}>
          <ExaminationPageLayout>
            {(model.details.status === 'TAKEN_BY' || model.details.status === 'DIAGNOSE_DRAFT') && (
              <UnassignFromVisitPageButton
                visitId={model.details.id ?? 0}
                onUnassigned={model.refetch}
              />
            )}
          </ExaminationPageLayout>

          {!webSocketData && (
            <ExaminationPageLayout>
              <AnamnesisSection
                examinationId={model.details.id ?? 0}
                createDate={model.details.createDate}
                mode={model.mode}
                isNotesCreateAndReadPermission={false}
                isNotesReadPermissionOnly={false}
                anamnesisTaggedQuestions={
                  model.answeredAnamnesisSurvey?.taggedQuestions
                }
                generalTaggedQuestions={
                  model.answeredGeneralSurvey?.taggedQuestions
                }
                hasCopyFeature
                hasToastWhenCopy={true}
                isVisitSection={true}
              />
            </ExaminationPageLayout>
          )}
          {temperatureExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="lifeSigns.temperature" />{' '}
                <FormattedMessage id="common.exams" defaultMessage="exams" />
              </Heading>
            </ConstraintContainer>
          )}
          <ExaminationPageLayout>
            <MeasurementContainer>
              {temperatureExams.length > 0 &&
                temperatureExams
                  .sort(
                    (a, b) =>
                      new Date(b.examination.sessionEndTime).getTime() -
                      new Date(a.examination.sessionEndTime).getTime(),
                  )
                  .slice(0, 10)
                  .map(({ examination, isCanceled }, index) => {
                    return (
                      <MeasurementItem
                        key={examination.examinationId}
                        id={`temperature-${index}`}
                      >
                        <ExamDate>
                          {examDate(examination.sessionEndTime)}
                        </ExamDate>

                        <TemperatureMeasurementEntry
                          examinationId={examination.examinationId}
                          isAvailable={true}
                          isVisitSection
                          isCanceledStatus={isCanceled}
                        />
                      </MeasurementItem>
                    );
                  })}
            </MeasurementContainer>
            {temperatureExams.length > 0 && <HorizontalDivider />}
          </ExaminationPageLayout>

          {bloodPressureExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage
                  id="common.bloodPressure"
                  defaultMessage="Blood pressure"
                />{' '}
                <FormattedMessage id="common.exams" defaultMessage="exams" />
              </Heading>
            </ConstraintContainer>
          )}
          <ExaminationPageLayout>
            <MeasurementContainer>
              {bloodPressureExams.length > 0 &&
                bloodPressureExams
                  .sort(
                    (a, b) =>
                      new Date(b.examination.sessionEndTime).getTime() -
                      new Date(a.examination.sessionEndTime).getTime(),
                  )
                  .slice(0, 10)
                  .map(({ examination, isCanceled }, index) => {
                    return (
                      <MeasurementItem
                        key={examination.examinationId}
                        id={`bloodPressure-${index}`}
                      >
                        <ExamDate>
                          {examDate(examination.sessionEndTime)}
                        </ExamDate>

                        <BloodPressureMeasurementEntry
                          examinationId={
                            examination.physicalExaminationDetails?.[0]
                              ?.physicalExaminationId ?? examination.examinationId
                          }
                          isAvailable={true}
                          isVisitSection
                          isCanceledStatus={isCanceled}
                        />
                      </MeasurementItem>
                    );
                  })}
            </MeasurementContainer>
            {bloodPressureExams.length > 0 && <HorizontalDivider />}
          </ExaminationPageLayout>

          {bloodSaturationExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage
                  id="common.bloodSaturation"
                  defaultMessage="Blood saturation"
                />{' '}
                <FormattedMessage id="common.exams" defaultMessage="exams" />
              </Heading>
            </ConstraintContainer>
          )}
          <ExaminationPageLayout>
            <MeasurementContainer>
              {bloodSaturationExams.length > 0 &&
                bloodSaturationExams
                  .sort(
                    (a, b) =>
                      new Date(b.examination.sessionEndTime).getTime() -
                      new Date(a.examination.sessionEndTime).getTime(),
                  )
                  .slice(0, 10)
                  .map(({ examination, isCanceled }, index) => {
                    return (
                      <MeasurementItem
                        key={examination.examinationId}
                        id={`bloodSaturation-${index}`}
                      >
                        <ExamDate>
                          {examDate(examination.sessionEndTime)}
                        </ExamDate>

                        <BloodSaturationMeasurementEntry
                          examinationId={examination.examinationId}
                          isAvailable={true}
                          isVisitSection
                          isCanceledStatus={isCanceled}
                        />
                      </MeasurementItem>
                    );
                  })}
            </MeasurementContainer>
            {bloodSaturationExams.length > 0 && <HorizontalDivider />}
          </ExaminationPageLayout>

          {heartRateExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="lifeSigns.pulse" />{' '}
                <FormattedMessage id="common.exams" defaultMessage="exams" />
              </Heading>
            </ConstraintContainer>
          )}
          <ExaminationPageLayout>
            <MeasurementContainer>
              {heartRateExams.length > 0 &&
                heartRateExams
                  .sort(
                    (a, b) =>
                      new Date(b.examination.sessionEndTime).getTime() -
                      new Date(a.examination.sessionEndTime).getTime(),
                  )
                  .slice(0, 10)
                  .map(({ examination, isCanceled }, index) => (
                    <MeasurementItem
                      key={examination.examinationId}
                      id={`heartRate-${index}`}
                    >
                      <ExamDate>
                        {examDate(examination.sessionEndTime)}
                      </ExamDate>

                      <HeartRateMeasurementEntry
                        isAvailable
                        examinationId={examination.examinationId}
                        isVisitSection
                        isCanceledStatus={isCanceled}
                      />
                    </MeasurementItem>
                  ))}
            </MeasurementContainer>
            {heartRateExams.length > 0 && <HorizontalDivider />}
          </ExaminationPageLayout>

          {skinExams.length > 0 && (
            <ConstraintContainer>
              {' '}
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.skinExamination.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}
          {skinExams.length > 0 &&
            skinExams
              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout key={examination.examinationId}>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <SkinExaminationSectionForVisits
                    isStatusCanceledByUser={isCanceled}
                    hasTogglingGalleryVideo
                    examinationId={examination.examinationId}
                    requestGenerateSkinExaminationVideoButtonSlot={
                      <RequestGenerateSkinExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}
          {skinExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {(heartExams.length > 0 || heartAsculationsPoints.length > 0) && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.heartAuscultation.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}
          {(heartExams.length > 0 || heartAsculationsPoints.length > 0) &&
            examIds.map(({ examId }) => {
              return (
                <VisitsLiveNewWebsocketExam
                  key={examId}
                  examId={examId}
                  model={model}
                  heartAsculationsPoints={heartAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                />
              );
            })}

          {(heartExams.length > 0 || heartAsculationsPoints.length > 0) &&
            heartExams

              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout key={examination.examinationId}>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <HeartAuscultationSection
                    isStatusCanceledByUser={isCanceled}
                    examinationId={examination.examinationId}
                    patientModel={model.patient}
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {(heartExams.length > 0 || heartAsculationsPoints.length > 0) && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {(lungsExams.length > 0 || lungsAsculationsPoints.length > 0) && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.lungAuscultation.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}
          {(lungsExams.length > 0 || lungsAsculationsPoints.length > 0) &&
            examIds.map(({ examId }) => {
              return (
                <VisitsLiveNewWebsocketExam
                  key={examId}
                  examId={examId}
                  model={model}
                  lungsAsculationsPoints={lungsAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                />
              );
            })}

          {(lungsExams.length > 0 || lungsAsculationsPoints.length > 0) &&
            lungsExams

              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout key={examination.examinationId}>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <LungsAuscultationSection
                    isStatusCanceledByUser={isCanceled}
                    examinationId={examination.examinationId}
                    patientModel={model.patient}
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {(lungsExams.length > 0 || lungsAsculationsPoints.length > 0) && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {coughExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.coughExamination.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}
          {coughExams.length > 0 &&
            coughExams
              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <CoughAuscultationSection
                    isStatusCanceledByUser={isCanceled}
                    examinationId={examination.examinationId}
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {coughExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {leftEarExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                {/* <FormattedMessage id="examinationPage.section.earsExamination.title" /> */}
                Left ear examination{' '}
              </Heading>
            </ConstraintContainer>
          )}

          {leftEarExams.length > 0 &&
            leftEarExams
              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <EarsExaminationSectionForVisits
                    isStatusCanceledByUser={isCanceled}
                    hasTogglingGalleryVideo
                    isVisitLeftEarExam
                    examinationId={examination.examinationId}
                    isPhysicalExaminationCompletedFn={
                      model.isPhysicalExaminationCompletedFn
                    }
                    requestGenerateLeftEarExaminationVideoButtonSlot={
                      <RequestGenerateLeftEarExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    requestGenerateRightEarExaminationVideoButtonSlot={
                      <RequestGenerateRightEarExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {leftEarExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {rightEarExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                {/* <FormattedMessage id="examinationPage.section.earsExamination.title" /> */}
                Right ear examination{' '}
              </Heading>
            </ConstraintContainer>
          )}

          {rightEarExams.length > 0 &&
            rightEarExams
              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <EarsExaminationSectionForVisits
                    isStatusCanceledByUser={isCanceled}
                    hasTogglingGalleryVideo
                    isVisitRightEarExam
                    examinationId={examination.examinationId}
                    isPhysicalExaminationCompletedFn={
                      model.isPhysicalExaminationCompletedFn
                    }
                    requestGenerateLeftEarExaminationVideoButtonSlot={
                      <RequestGenerateLeftEarExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    requestGenerateRightEarExaminationVideoButtonSlot={
                      <RequestGenerateRightEarExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {rightEarExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {throatExams.length > 0 && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.throatExamination.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}

          {throatExams.length > 0 &&
            throatExams
              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <ThroatExaminationSectionForVisits
                    isStatusCanceledByUser={isCanceled}
                    examinationId={examination.examinationId}
                    hasTogglingGalleryVideo
                    requestGenerateThroatExaminationVideoButtonSlot={
                      <RequestGenerateThroatExaminationVideoButton
                        examinationId={examination.examinationId}
                      />
                    }
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {throatExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}

          {(abdominalExams.length > 0 ||
            abdominalAsculationsPoints.length > 0) && (
            <ConstraintContainer>
              <Heading level={3}>
                <FormattedMessage id="examinationPage.section.abdominalAuscultation.title" />{' '}
              </Heading>
            </ConstraintContainer>
          )}
          {(abdominalExams.length > 0 ||
            abdominalAsculationsPoints.length > 0) &&
            examIds.map(({ examId }) => {
              return (
                <VisitsLiveNewWebsocketExam
                  key={examId}
                  examId={examId}
                  model={model}
                  abdominalAsculationsPoints={abdominalAsculationsPoints.filter(
                    (point) => point.examId === examId,
                  )}
                />
              );
            })}

          {(abdominalExams.length > 0 ||
            abdominalAsculationsPoints.length > 0) &&
            abdominalExams

              .sort(
                (a, b) =>
                  new Date(b.examination.sessionEndTime).getTime() -
                  new Date(a.examination.sessionEndTime).getTime(),
              )

              .map(({ examination, isCanceled }) => (
                <ExaminationPageLayout key={examination.examinationId}>
                  <ExamDate>{examDate(examination.sessionEndTime)}</ExamDate>

                  <AbdominalAuscultationSection
                    examinationId={examination.examinationId}
                    isStatusCanceledByUser={isCanceled}
                    isVisitSection
                  />
                </ExaminationPageLayout>
              ))}

          {abdominalExams.length > 0 && (
            <ExaminationPageLayout>
              <HorizontalDivider />
            </ExaminationPageLayout>
          )}
          <ExaminationPageLayout>
            {model.details.status === 'IN_QUEUE' ? (
              <AssignFromVisitPageButton
                visitId={model.details.id ?? 0}
                shouldBeInvisible={!assignmentPermission}
                onAssigned={model.refetch}
              />
            ) : (
              <ExaminationPageSummarySection
                patientOverview={model.patientOverview}
                isReadOnly={model.details.status === 'ENDED'}
                isVisitSection
              >
                {model.diagnosis.isEditable && (
                  <>
                    <DraftButtonContainer>
                      <Button
                        color="secondary"
                        autoWidth
                        endIcon={<SaveIcon />}
                        onClick={onSaveDraftButtonClick}
                        isLoading={'saving' === autoSaveState}
                        hidden={true}
                        style={{ display: 'none' }}
                      >
                        <FormattedMessage id="examinationPage.button.saveAsDraft" />
                      </Button>
                    </DraftButtonContainer>
                    <SendButtonContainer>
                      <Button
                        type="submit"
                        fullWidth
                        isLoading={false}
                        style={{ marginTop: '0.6rem' }}
                        disabled={model.details.status === 'ENDED'}
                      >
                        <FormattedMessage id="common.send" />
                      </Button>
                    </SendButtonContainer>
                  </>
                )}
              </ExaminationPageSummarySection>
            )}
          </ExaminationPageLayout>
        </form>
      </FormProvider>

      {webSocketData && (
        <>
          <ExaminationPageLayout>
            {' '}
            {examIds.length > 6 && (
              <CloseVisitButton visitId={visitId}></CloseVisitButton>
            )}
          </ExaminationPageLayout>
        </>
      )}
    </VisitsContainer>
  ) : (
    <></>
  );
};
